/**
 * Image Processing Service
 * 
 * Handles branding application using Canvas API and Supabase uploads.
 */

import { supabase, STORAGE_BUCKET } from '@/lib/supabase';
import { AspectRatio, LogoPosition, WatermarkPosition } from '@/types';
import {
  DEFAULT_AI_LABEL_OPACITY,
  DEFAULT_AI_LABEL_POSITION,
  clampAiLabelOpacity,
  type AiLabelPosition,
} from '@/lib/aiLabelPrefs';

// ============================================================================
// Image Loading
// ============================================================================

export const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
  });
};

/**
 * Fetches a remote image and returns a local blob URL.
 * This avoids CORS/canvas-taint issues when the image needs to be
 * drawn on a canvas (e.g., for cropping or branding).
 */
export const fetchImageAsBlob = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

// ============================================================================
// Watermark Processing
// ============================================================================

export interface WatermarkOptions {
  opacity: number; // 0-100
  position: WatermarkPosition;
  scale: number; // 0.1-1.0
}

export interface WatermarkPixelStats {
  visible: number;
  total: number;
}

/**
 * Keys the raw RGBA pixels of a watermark source in place. Two modes:
 * - Source with transparency (PNG mit Alpha): the alpha channel is the shape;
 *   colors stay untouched, alpha is multiplied by the target opacity.
 * - Fully opaque source (classic dark-logo-on-white): luminance keying with an
 *   adaptive threshold between lightest (background) and darkest (mark) pixel,
 *   so light-gray or colored logos survive instead of vanishing.
 */
export const keyWatermarkPixels = (
  data: Uint8ClampedArray,
  opacity: number // 0-100
): WatermarkPixelStats => {
  const targetAlpha = Math.round((opacity / 100) * 255);
  const total = data.length / 4;
  let visible = 0;

  let hasAlpha = false;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 128) {
      hasAlpha = true;
      break;
    }
  }

  if (hasAlpha) {
    for (let i = 0; i < data.length; i += 4) {
      const a = Math.round((data[i + 3] / 255) * targetAlpha);
      data[i + 3] = a;
      if (a >= 8) visible++;
    }
    return { visible, total };
  }

  let minLum = 255;
  let maxLum = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (lum < minLum) minLum = lum;
    if (lum > maxLum) maxLum = lum;
  }
  const range = maxLum - minLum;
  if (range < 16) {
    // Flat single-color image — no separable mark.
    return { visible: 0, total };
  }

  // Pixels close to the lightest value count as background; everything
  // meaningfully darker is part of the mark, weighted by relative darkness.
  // (For black-on-white this matches the previous fixed 180-threshold.)
  const threshold = maxLum - range * 0.3;
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (lum > threshold) {
      data[i + 3] = 0;
    } else {
      const darkness = (threshold - lum) / (threshold - minLum);
      const a = Math.round(targetAlpha * darkness);
      data[i + 3] = a;
      if (a >= 8) visible++;
    }
  }
  return { visible, total };
};

/**
 * Process a watermark image into a stamp canvas (keying rules in
 * keyWatermarkPixels). Throws when the result would be invisible, so callers
 * can surface the problem instead of silently exporting without watermark.
 */
export const processWatermark = async (
  watermarkUrl: string,
  targetWidth: number,
  _targetHeight: number, // Reserved for future use (e.g., different scaling modes)
  options: WatermarkOptions
): Promise<HTMLCanvasElement> => {
  const watermarkImg = await loadImage(watermarkUrl);

  // Calculate watermark dimensions based on scale
  const wmWidth = targetWidth * options.scale;
  const wmHeight = (watermarkImg.naturalHeight / watermarkImg.naturalWidth) * wmWidth;

  // Create a canvas to process the watermark
  const wmCanvas = document.createElement('canvas');
  wmCanvas.width = wmWidth;
  wmCanvas.height = wmHeight;
  const wmCtx = wmCanvas.getContext('2d');

  if (!wmCtx) {
    throw new Error('Could not get watermark canvas context');
  }

  // Draw the watermark image
  wmCtx.drawImage(watermarkImg, 0, 0, wmWidth, wmHeight);

  // Get image data for pixel manipulation
  const imageData = wmCtx.getImageData(0, 0, wmCanvas.width, wmCanvas.height);
  const { visible, total } = keyWatermarkPixels(imageData.data, options.opacity);

  if (visible === 0 || visible / total < 0.001) {
    throw new Error('Wasserzeichen hat nach der Verarbeitung keine sichtbaren Pixel');
  }

  // Put the processed data back
  wmCtx.putImageData(imageData, 0, 0);

  return wmCanvas;
};

/**
 * Draw watermark on the main canvas with specified position
 */
export const drawWatermark = (
  ctx: CanvasRenderingContext2D,
  watermarkCanvas: HTMLCanvasElement,
  canvasWidth: number,
  canvasHeight: number,
  position: WatermarkPosition
): void => {
  const wmWidth = watermarkCanvas.width;
  const wmHeight = watermarkCanvas.height;
  const padding = canvasWidth * 0.05; // 5% padding

  if (position === 'tile') {
    // Tile the watermark across the entire canvas
    const cols = Math.ceil(canvasWidth / wmWidth) + 1;
    const rows = Math.ceil(canvasHeight / wmHeight) + 1;
    const offsetX = (canvasWidth - (cols * wmWidth)) / 2;
    const offsetY = (canvasHeight - (rows * wmHeight)) / 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = offsetX + col * wmWidth;
        const y = offsetY + row * wmHeight;
        ctx.drawImage(watermarkCanvas, x, y);
      }
    }
  } else {
    // Single watermark at specified position
    let x = 0;
    let y = 0;

    switch (position) {
      case 'center':
        x = (canvasWidth - wmWidth) / 2;
        y = (canvasHeight - wmHeight) / 2;
        break;
      case 'tl': // top-left
        x = padding;
        y = padding;
        break;
      case 'tr': // top-right
        x = canvasWidth - wmWidth - padding;
        y = padding;
        break;
      case 'bl': // bottom-left
        x = padding;
        y = canvasHeight - wmHeight - padding;
        break;
      case 'br': // bottom-right
        x = canvasWidth - wmWidth - padding;
        y = canvasHeight - wmHeight - padding;
        break;
    }

    ctx.drawImage(watermarkCanvas, x, y);
  }
};

// ============================================================================
// Branding Application
// ============================================================================

export interface BrandingOptions {
  logoUrl?: string | null;
  overlayUrl?: string | null;
  logoPosition?: LogoPosition;
  watermarkUrl?: string | null;
  watermarkOpacity?: number;
  watermarkPosition?: WatermarkPosition;
  watermarkScale?: number;
}

export type BrandingLayer = 'watermark' | 'overlay' | 'logo' | 'ai-label';

export interface BrandingResult {
  url: string; // data URL on success, base URL when compositing failed entirely
  appliedLayers: BrandingLayer[];
  failedLayers: BrandingLayer[]; // configured layers that could not be applied
}

/**
 * Composites the configured branding layers onto the base image and reports
 * per-layer success. Callers persisting/naming the result as "branded" MUST
 * check failedLayers — a failed layer means the pixels are NOT in the output.
 */
export const applyBrandingDetailed = async (
  baseImageUrl: string,
  options: BrandingOptions
): Promise<BrandingResult> => {
  const appliedLayers: BrandingLayer[] = [];
  const failedLayers: BrandingLayer[] = [];

  try {
    const baseImg = await loadImage(baseImageUrl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Set canvas dimensions to match base image
    canvas.width = baseImg.naturalWidth;
    canvas.height = baseImg.naturalHeight;

    // 1. Draw base image
    ctx.drawImage(baseImg, 0, 0);

    // 2. Draw watermark (between base and overlay/logo)
    // NOTE: Watermark-only branding is fully supported - no logo/overlay required
    if (options.watermarkUrl) {
      try {
        const wmOptions: WatermarkOptions = {
          opacity: options.watermarkOpacity ?? 30,
          position: options.watermarkPosition ?? 'center',
          scale: options.watermarkScale ?? 0.5,
        };

        const watermarkCanvas = await processWatermark(
          options.watermarkUrl,
          canvas.width,
          canvas.height,
          wmOptions
        );

        drawWatermark(ctx, watermarkCanvas, canvas.width, canvas.height, wmOptions.position);
        appliedLayers.push('watermark');
      } catch (e) {
        console.warn('[Branding] Failed to process watermark:', e);
        failedLayers.push('watermark');
      }
    }

    // 3. Draw overlay (stretched to fit, for 4:3 frames)
    if (options.overlayUrl) {
      try {
        const overlayImg = await loadImage(options.overlayUrl);
        ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);
        appliedLayers.push('overlay');
      } catch (e) {
        console.warn('Failed to load overlay:', e);
        failedLayers.push('overlay');
      }
    }

    // 4. Draw logo with positioning
    if (options.logoUrl) {
      try {
        const logoImg = await loadImage(options.logoUrl);

        // Calculate logo size (15% of canvas width)
        const targetWidth = canvas.width * 0.15;
        const scale = targetWidth / logoImg.naturalWidth;
        const targetHeight = logoImg.naturalHeight * scale;

        // Calculate padding (3% of canvas)
        const padding = canvas.width * 0.03;

        // Determine position
        let x = 0;
        let y = 0;
        const pos = options.logoPosition ?? 'bottom-right';

        switch (pos) {
          case 'top-left':
            x = padding;
            y = padding;
            break;
          case 'top-right':
            x = canvas.width - targetWidth - padding;
            y = padding;
            break;
          case 'bottom-left':
            x = padding;
            y = canvas.height - targetHeight - padding;
            break;
          case 'bottom-right':
            x = canvas.width - targetWidth - padding;
            y = canvas.height - targetHeight - padding;
            break;
        }

        // Draw logo with drop shadow
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 10;
        ctx.drawImage(logoImg, x, y, targetWidth, targetHeight);
        ctx.restore();
        appliedLayers.push('logo');
      } catch (e) {
        console.warn('Failed to load logo:', e);
        failedLayers.push('logo');
      }
    }

    return { url: canvas.toDataURL('image/png'), appliedLayers, failedLayers };
  } catch (error) {
    console.error('Branding failed:', error);
    // Fallback to original image if branding fails — every configured layer
    // counts as failed so callers don't persist the result as "branded".
    const configured: BrandingLayer[] = [];
    if (options.watermarkUrl) configured.push('watermark');
    if (options.overlayUrl) configured.push('overlay');
    if (options.logoUrl) configured.push('logo');
    return { url: baseImageUrl, appliedLayers: [], failedLayers: configured };
  }
};

/** Ein konfigurierter Branding-Layer konnte nicht eingebrannt werden. */
export class BrandingLayerError extends Error {
  readonly failedLayers: BrandingLayer[];

  constructor(failedLayers: BrandingLayer[]) {
    super(`Branding-Layer konnten nicht angewendet werden: ${failedLayers.join(', ')}`);
    this.name = 'BrandingLayerError';
    this.failedLayers = failedLayers;
  }
}

/**
 * Gemeinsamer Export-Kern aller ZIP-Flows: Branding einbrennen (fail-loud via
 * BrandingLayerError) und optional portal-freundlich komprimieren.
 */
export const renderBrandedExport = async (
  source: string,
  options: BrandingOptions,
  compress?: { aspectRatio: AspectRatio }
): Promise<string> => {
  const result = await applyBrandingDetailed(source, options);
  if (result.failedLayers.length > 0) {
    throw new BrandingLayerError(result.failedLayers);
  }
  return compress ? compressForExport(result.url, compress.aspectRatio) : result.url;
};

export const applyBranding = async (
  baseImageUrl: string,
  logoUrlOrOptions: string | null | BrandingOptions,
  overlayUrl?: string | null,
  logoPosition: LogoPosition = 'bottom-right'
): Promise<string> => {
  // Support both old signature and new options object
  let options: BrandingOptions;

  if (typeof logoUrlOrOptions === 'object' && logoUrlOrOptions !== null && !('naturalWidth' in logoUrlOrOptions)) {
    // New signature with options object
    options = logoUrlOrOptions as BrandingOptions;
  } else {
    // Legacy signature
    options = {
      logoUrl: logoUrlOrOptions as string | null,
      overlayUrl,
      logoPosition,
    };
  }

  const result = await applyBrandingDetailed(baseImageUrl, options);
  return result.url;
};

// ============================================================================
// AI Label ("Mit KI erstellt")
// ============================================================================

/** German wording is deliberate — the badge is shown to buyers, not to us. */
export const AI_LABEL_TEXT = 'Mit KI erstellt';

/** System font stack: no font asset has to be loaded before drawing. */
const AI_LABEL_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** JPEG re-encode quality — top step of compressForExport, keeps the loss low. */
const AI_LABEL_JPEG_QUALITY = 0.92;

export interface AiLabelMetrics {
  fontSize: number;
  /** Ready-to-assign ctx.font value. */
  font: string;
  paddingX: number;
  paddingY: number;
  margin: number;
}

/**
 * Label geometry relative to the image width, so the badge carries the same
 * visual weight on a 1200px preview and on a 4000px export. The 14px floor
 * keeps it readable on small tryout images.
 */
export const aiLabelMetrics = (canvasWidth: number): AiLabelMetrics => {
  const fontSize = Math.max(14, Math.round(canvasWidth * 0.018));
  return {
    fontSize,
    font: `500 ${fontSize}px ${AI_LABEL_FONT_STACK}`,
    paddingX: Math.round(fontSize * 0.75),
    paddingY: Math.round(fontSize * 0.5),
    margin: Math.max(10, Math.round(canvasWidth * 0.015)),
  };
};

/** Rounded rectangle path (ctx.roundRect is not available everywhere). */
const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
};

/**
 * Draws the discreet "Mit KI erstellt" pill into one of the four corners.
 * Semi-transparent dark background with white text, so it stays legible on
 * bright and dark photos alike without competing with the branding layers.
 *
 * `opacity` (0.3–1.0, default 1.0) fades pill and text together via
 * globalAlpha — the base values below stay the 100 % look.
 */
export const drawAiLabel = (
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  position: AiLabelPosition = DEFAULT_AI_LABEL_POSITION,
  opacity: number = DEFAULT_AI_LABEL_OPACITY
): void => {
  const { font, fontSize, paddingX, paddingY, margin } = aiLabelMetrics(canvasWidth);

  ctx.save();
  ctx.globalAlpha = clampAiLabelOpacity(opacity);
  ctx.font = font;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  const textWidth = ctx.measureText(AI_LABEL_TEXT).width;
  const pillWidth = textWidth + paddingX * 2;
  const pillHeight = fontSize + paddingY * 2;
  const isLeft = position === 'top-left' || position === 'bottom-left';
  const isTop = position === 'top-left' || position === 'top-right';
  const x = isLeft ? margin : Math.max(margin, canvasWidth - pillWidth - margin);
  const y = isTop ? margin : Math.max(margin, canvasHeight - pillHeight - margin);

  ctx.fillStyle = 'rgba(17, 17, 17, 0.55)';
  roundedRectPath(ctx, x, y, pillWidth, pillHeight, pillHeight / 2);
  ctx.fill();

  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillText(AI_LABEL_TEXT, x + paddingX, y + pillHeight / 2);
  ctx.restore();
};

/**
 * Output format of a labelled image — a JPG export must not silently become a
 * PNG (portals limit file sizes, and the ZIP name still says .jpg).
 */
export const aiLabelOutputMime = (source: string | Blob): 'image/png' | 'image/jpeg' => {
  if (typeof source !== 'string') {
    return source.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  }
  if (/^data:image\/jpe?g/i.test(source)) return 'image/jpeg';
  return /\.jpe?g([?#]|$)/i.test(source) ? 'image/jpeg' : 'image/png';
};

/** Draws source + label onto a canvas at full resolution. Shared by both output forms. */
const renderAiLabelCanvas = async (
  source: string | Blob,
  position: AiLabelPosition,
  opacity: number
): Promise<{ canvas: HTMLCanvasElement; mime: 'image/png' | 'image/jpeg' }> => {
  const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
  try {
    const img = await loadImage(objectUrl ?? (source as string));
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    ctx.drawImage(img, 0, 0);
    drawAiLabel(ctx, canvas.width, canvas.height, position, opacity);

    return { canvas, mime: aiLabelOutputMime(source) };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Loads an image, burns the AI label into it and returns it as a Blob in the
 * source format. Applied as the last export step — after branding, so the
 * badge is never hidden below a watermark or overlay.
 */
export const applyAiLabelToImage = async (
  source: string | Blob,
  position: AiLabelPosition = DEFAULT_AI_LABEL_POSITION,
  opacity: number = DEFAULT_AI_LABEL_OPACITY
): Promise<Blob> => {
  const { canvas, mime } = await renderAiLabelCanvas(source, position, opacity);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('KI-Hinweis konnte nicht gerendert werden'))),
      mime,
      mime === 'image/jpeg' ? AI_LABEL_JPEG_QUALITY : undefined
    );
  });
};

/** Same rendering as the export, but as a data URL for the on-screen preview. */
export const applyAiLabelToDataUrl = async (
  source: string | Blob,
  position: AiLabelPosition = DEFAULT_AI_LABEL_POSITION,
  opacity: number = DEFAULT_AI_LABEL_OPACITY
): Promise<string> => {
  const { canvas, mime } = await renderAiLabelCanvas(source, position, opacity);
  return canvas.toDataURL(mime, mime === 'image/jpeg' ? AI_LABEL_JPEG_QUALITY : undefined);
};

/** What the AI label adds on top of the branding — see renderExportPreview. */
export interface AiLabelPreviewOptions {
  enabled: boolean;
  position: AiLabelPosition;
  opacity: number;
}

/**
 * The single renderer behind every "So sieht dein Export aus" preview: branding
 * first, then the AI label — exactly the order the export uses.
 *
 * Preview and export MUST go through the same composition, otherwise a layer
 * added to one silently goes missing in the other (which is how the AI label
 * first shipped invisible in the previews). Call this instead of chaining
 * applyBrandingDetailed by hand.
 *
 * A failing label does not void the preview: the branded image is still shown
 * and 'ai-label' is reported in failedLayers, mirroring the branding layers.
 */
export const renderExportPreview = async (
  baseImageUrl: string,
  brandingOptions: BrandingOptions,
  aiLabel?: AiLabelPreviewOptions,
  deps: {
    brand?: (url: string, options: BrandingOptions) => Promise<BrandingResult>;
    label?: (source: string, position: AiLabelPosition, opacity: number) => Promise<string>;
  } = {}
): Promise<BrandingResult> => {
  const brand = deps.brand ?? applyBrandingDetailed;
  const label = deps.label ?? applyAiLabelToDataUrl;

  const branded = await brand(baseImageUrl, brandingOptions);
  if (!aiLabel?.enabled) return branded;

  try {
    const url = await label(branded.url, aiLabel.position, aiLabel.opacity);
    return { ...branded, url, appliedLayers: [...branded.appliedLayers, 'ai-label'] };
  } catch (error) {
    console.error('[renderExportPreview] AI label failed:', error);
    return { ...branded, failedLayers: [...branded.failedLayers, 'ai-label'] };
  }
};

// ============================================================================
// Image Utilities
// ============================================================================

/**
 * Downscales an image so its longest edge fits maxDim — for fast preview
 * compositing on the main thread (full-res previews freeze the page on
 * large uploads). Returns the original URL unchanged when it already fits;
 * otherwise a blob object URL the caller should cache and revoke.
 */
export const downscaleForPreview = async (url: string, maxDim = 1600): Promise<string> => {
  const img = await loadImage(url);
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  if (scale >= 1) return url;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return url;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b ? URL.createObjectURL(b) : url), 'image/jpeg', 0.9);
  });
};

/**
 * Converts a data URL to a Blob
 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * Crops an image to a 1:1 aspect ratio (center crop)
 */
export const cropToSquare = async (imageUrl: string): Promise<string> => {
  const img = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const size = Math.min(img.naturalWidth, img.naturalHeight);
  canvas.width = size;
  canvas.height = size;

  const offsetX = (img.naturalWidth - size) / 2;
  const offsetY = (img.naturalHeight - size) / 2;

  ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);

  return canvas.toDataURL('image/png');
};

export interface CropOutputOptions {
  /** Longest edge of the result — full-res crops of large photos freeze the
   * page (PNG-Encoding + riesige Data-URLs im State); Previews downscalen. */
  maxDim?: number;
  format?: 'png' | 'jpeg';
  quality?: number;
  /** Return a blob object URL instead of a data URL (caller revokes). */
  asBlobUrl?: boolean;
}

/**
 * Crops an image to the specified aspect ratio.
 * @param imageUrl - Source image URL (blob, data URL, or remote)
 * @param aspectRatio - Target aspect ratio ('1:1' or '4:3')
 * @param offsetPercent - Crop offset (0-1 for x and y, 0.5 = center)
 * @param scale - Crop scale (0.1-1.0, 1 = max crop area, smaller = zoom in)
 * @param output - Optional output sizing/encoding (default: full-res PNG data URL)
 * @returns Data URL (or blob URL) of the cropped image
 */
export const cropToAspectRatio = async (
  imageUrl: string,
  aspectRatio: AspectRatio,
  offsetPercent: { x: number; y: number } = { x: 0.5, y: 0.5 },
  scale: number = 1.0,
  output?: CropOutputOptions
): Promise<string> => {
  const img = await loadImage(imageUrl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Could not get canvas context');

  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const targetRatio = aspectRatio === '1:1' ? 1 : 4 / 3;
  const srcRatio = srcW / srcH;

  // Compute maximum crop area at target ratio
  let maxCropW: number;
  let maxCropH: number;

  if (srcRatio > targetRatio) {
    maxCropH = srcH;
    maxCropW = Math.round(srcH * targetRatio);
  } else {
    maxCropW = srcW;
    maxCropH = Math.round(srcW / targetRatio);
  }

  // Apply scale
  const s = Math.max(0.1, Math.min(1, scale));
  const cropW = Math.round(maxCropW * s);
  const cropH = Math.round(maxCropH * s);

  // Clamp offset so crop stays within bounds
  const maxOffsetX = srcW - cropW;
  const maxOffsetY = srcH - cropH;
  const ox = Math.max(0, Math.min(1, offsetPercent.x));
  const oy = Math.max(0, Math.min(1, offsetPercent.y));
  const cropX = Math.round(maxOffsetX * ox);
  const cropY = Math.round(maxOffsetY * oy);

  // Optional downscale of the result (single drawImage, no second canvas)
  const outScale = output?.maxDim ? Math.min(1, output.maxDim / Math.max(cropW, cropH)) : 1;
  const outW = Math.max(1, Math.round(cropW * outScale));
  const outH = Math.max(1, Math.round(cropH * outScale));

  canvas.width = outW;
  canvas.height = outH;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

  const mime = output?.format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const quality = output?.quality ?? 0.9;
  if (output?.asBlobUrl) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(URL.createObjectURL(b)) : reject(new Error('Crop toBlob failed'))),
        mime,
        quality
      );
    });
  }
  return canvas.toDataURL(mime, quality);
};

/**
 * Checks whether an image matches the given aspect ratio (within a small tolerance).
 */
export const imageMatchesAspectRatio = async (
  imageUrl: string,
  aspectRatio: AspectRatio
): Promise<boolean> => {
  const img = await loadImage(imageUrl);
  const targetRatio = aspectRatio === '1:1' ? 1 : 4 / 3;
  const srcRatio = img.naturalWidth / img.naturalHeight;
  return Math.abs(srcRatio - targetRatio) < 0.05;
};

// ============================================================================
// Export Compression
// ============================================================================

const COMPRESSED_DIMENSIONS: Record<AspectRatio, { width: number; height: number }> = {
  '4:3': { width: 2400, height: 1800 },
  '1:1': { width: 2400, height: 2400 },
};

const MAX_EXPORT_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Compresses an image for portal-friendly export.
 * Resizes to uniform dimensions (2400x1800 for 4:3, 2400x2400 for 1:1)
 * and outputs JPEG with quality stepping to guarantee < 5MB.
 */
export const compressForExport = async (
  imageDataUrl: string,
  aspectRatio: AspectRatio
): Promise<string> => {
  const img = await loadImage(imageDataUrl);
  const { width: targetW, height: targetH } = COMPRESSED_DIMENSIONS[aspectRatio];

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  canvas.width = targetW;
  canvas.height = targetH;

  // Use high-quality image smoothing for downscaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // Quality stepping: start high, reduce if > 5MB
  const qualitySteps = [0.92, 0.85, 0.78, 0.70];

  for (const quality of qualitySteps) {
    const dataUrl = await new Promise<string>((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob && blob.size <= MAX_EXPORT_FILE_SIZE) {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          } else {
            resolve(''); // Signal to try lower quality
          }
        },
        'image/jpeg',
        quality
      );
    });

    if (dataUrl) {
      console.log(`[Compress] Output: ${targetW}x${targetH} JPEG @ q=${quality}`);
      return dataUrl;
    }
  }

  // Final fallback: lowest quality
  return new Promise<string>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to compress image'));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      0.60
    );
  });
};

// ============================================================================
// Supabase Image Upload
// ============================================================================

export interface UploadResult {
  success: boolean;
  publicUrl?: string;
  storagePath?: string;
  imageId?: string;
  error?: string;
}

/**
 * Upload a base64 image to Supabase Storage and create/update a database entry
 * 
 * @param projectId - The project ID to associate the image with
 * @param imageBase64 - The base64 encoded image (data:image/png;base64,...)
 * @param roomType - The room type label
 * @param suffix - The image suffix (e.g., 'empty', 'staged', 'branded', 'branded_empty')
 * @param existingImageId - Optional: If provided, update this record instead of searching
 * @returns The public URL of the uploaded image
 */
export const uploadImageToSupabase = async (
  projectId: string,
  imageBase64: string,
  roomType: string,
  suffix: string,
  existingImageId?: string // NEW: Direct ID for updating
): Promise<UploadResult> => {
  try {
    // 1. Convert base64 to Blob
    const blob = dataUrlToBlob(imageBase64);

    // 2. Create unique filename
    const timestamp = Date.now();
    const sanitizedRoomType = roomType.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const fileName = `${timestamp}_${sanitizedRoomType}_${suffix}.png`;
    const storagePath = `${projectId}/${fileName}`;

    // 3. Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, blob, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'image/png',
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      return { success: false, error: `Upload fehlgeschlagen: ${uploadError.message}` };
    }

    // 4. Get public URL
    const { data: urlData } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(storagePath);

    const publicUrl = urlData.publicUrl;

    // 5. Determine which field to update based on suffix
    let updateField: string;
    switch (suffix) {
      case 'empty':
        updateField = 'storage_path_empty';
        break;
      case 'staged':
        updateField = 'storage_path_staged';
        break;
      case 'branded':
        updateField = 'storage_path_branded';
        break;
      case 'branded_empty':
        updateField = 'storage_path_branded_empty';
        break;
      default:
        updateField = 'storage_path';
    }

    // 6. Update or create database entry
    let imageId: string;

    // If an existing image ID is provided, use it directly
    if (existingImageId) {
      imageId = existingImageId;
      const { error: updateError } = await supabase
        .from('images')
        .update({
          [updateField]: storagePath,
          status: 'completed',
        })
        .eq('id', imageId);

      if (updateError) {
        console.warn('Database update error:', updateError);
      }
    } else {
      // Fallback: Check if an image entry for this room already exists
      const { data: existingImage } = await supabase
        .from('images')
        .select('id')
        .eq('project_id', projectId)
        .eq('room_type', roomType)
        .maybeSingle();

      if (existingImage) {
        // Update existing record
        imageId = existingImage.id;
        const { error: updateError } = await supabase
          .from('images')
          .update({
            [updateField]: storagePath,
            status: 'completed',
          })
          .eq('id', imageId);

        if (updateError) {
          console.warn('Database update error:', updateError);
        }
      } else {
        // Create new record
        const { data: newImage, error: insertError } = await supabase
          .from('images')
          .insert({
            project_id: projectId,
            room_type: roomType,
            storage_path: storagePath, // Use as primary path too
            [updateField]: storagePath,
            status: 'completed',
          })
          .select('id')
          .single();

        if (insertError) {
          console.warn('Database insert error:', insertError);
          // Still return success since the file was uploaded
          return { success: true, publicUrl, storagePath };
        }

        imageId = newImage.id;
      }
    }

    console.log(`[Upload] Saved ${suffix} image for ${roomType}: ${storagePath}`);

    return {
      success: true,
      publicUrl,
      storagePath,
      imageId,
    };
  } catch (error) {
    console.error('Upload to Supabase failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unbekannter Fehler',
    };
  }
};

/**
 * Upload multiple branded images to Supabase (batch operation)
 * Supports both branded staged and branded empty versions
 */
export const uploadBrandedImagesToSupabase = async (
  projectId: string,
  images: Array<{ 
    base64: string; 
    roomType: string; 
    originalId: string;
    variant: 'staged' | 'empty'; // NEW: Specify which variant is being branded
  }>
): Promise<{ uploaded: number; failed: number }> => {
  let uploaded = 0;
  let failed = 0;

  for (const img of images) {
    // Determine suffix based on variant
    const suffix = img.variant === 'empty' ? 'branded_empty' : 'branded';
    
    const result = await uploadImageToSupabase(
      projectId,
      img.base64,
      img.roomType,
      suffix,
      img.originalId // Pass the ID to update the correct record
    );

    if (result.success) {
      uploaded++;
    } else {
      failed++;
    }
  }

  return { uploaded, failed };
};
